import { useEffect, useState } from 'react';
import { usersApi, organizationApi, OrgUser } from '../services/api';
import { Organization, OrganizationRole } from '@dsgvo-buddy/types';
import {
  Title,
  Button,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Input,
  Select,
  Option,
  FlexBox,
  Text,
  ObjectStatus,
  BusyIndicator,
  Card,
  CardHeader,
  Dialog,
  Bar,
  Label,
  MessageStrip,
  Icon
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

const roleConfig: Record<OrganizationRole, { label: string; state: string }> = {
  [OrganizationRole.OWNER]: { label: 'Inhaber', state: 'Success' },
  [OrganizationRole.ADMIN]: { label: 'Administrator', state: 'Information' },
  [OrganizationRole.MEMBER]: { label: 'Mitglied', state: 'Warning' },
  [OrganizationRole.VIEWER]: { label: 'Betrachter', state: 'None' }
};

const permissionsTable = [
  { action: 'Inhalte lesen', owner: true, admin: true, member: true, viewer: true },
  { action: 'Inhalte erstellen', owner: true, admin: true, member: true, viewer: false },
  { action: 'Inhalte bearbeiten', owner: true, admin: true, member: true, viewer: false },
  { action: 'Status genehmigen', owner: true, admin: true, member: false, viewer: false },
  { action: 'Inhalte löschen', owner: true, admin: true, member: false, viewer: false },
  { action: 'AVV-Dokumente verwalten', owner: true, admin: true, member: false, viewer: false },
  { action: 'Organisation löschen', owner: true, admin: false, member: false, viewer: false },
  { action: 'Benutzerverwaltung', owner: true, admin: false, member: false, viewer: false },
];

interface UserFormData {
  name: string;
  email: string;
  password: string;
  organization_role: OrganizationRole;
}

const emptyForm: UserFormData = {
  name: '',
  email: '',
  password: '',
  organization_role: OrganizationRole.MEMBER
};

export default function UserManagementPage() {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<OrgUser | null>(null);
  const [formData, setFormData] = useState<UserFormData>(emptyForm);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const orgResponse = await organizationApi.getAll();
      if (orgResponse.success && orgResponse.data && orgResponse.data.length > 0) {
        const org = orgResponse.data[0];
        setOrganization(org);
        const usersResponse = await usersApi.getByOrganization(org.id);
        if (usersResponse.success && usersResponse.data) {
          setUsers(usersResponse.data);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData(emptyForm);
    setError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (user: OrgUser) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      organization_role: user.organization_role
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!organization) return;
    setError('');

    if (!formData.name || !formData.email) {
      setError('Name und E-Mail sind erforderlich');
      return;
    }

    try {
      if (editingUser) {
        const updateData: any = {
          organization_id: organization.id,
          name: formData.name,
          email: formData.email,
          organization_role: formData.organization_role
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        const response = await usersApi.update(editingUser.id, updateData);
        if (!response.success) {
          setError(response.error || 'Fehler beim Aktualisieren');
          return;
        }
        setSuccessMsg('Benutzer erfolgreich aktualisiert');
      } else {
        if (!formData.password || formData.password.length < 6) {
          setError('Passwort muss mindestens 6 Zeichen lang sein');
          return;
        }
        const response = await usersApi.create({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          organization_role: formData.organization_role,
          organization_id: organization.id
        });
        if (!response.success) {
          setError(response.error || 'Fehler beim Erstellen');
          return;
        }
        setSuccessMsg('Benutzer erfolgreich erstellt');
      }

      setDialogOpen(false);
      loadData();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch {
      setError('Ein Fehler ist aufgetreten');
    }
  };

  const handleDelete = async (user: OrgUser) => {
    if (!organization) return;
    if (!confirm(`Benutzer "${user.name}" wirklich entfernen?`)) return;

    const response = await usersApi.delete(user.id, organization.id);
    if (response.success) {
      setSuccessMsg('Benutzer erfolgreich entfernt');
      loadData();
      setTimeout(() => setSuccessMsg(''), 3000);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <BusyIndicator active size="Large" />
      </div>
    );
  }

  return (
    <div className="page-layout">
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <Title level="H3">Benutzerverwaltung</Title>
        <Button design="Emphasized" icon="add" onClick={handleOpenCreate}>
          Benutzer anlegen
        </Button>
      </FlexBox>

      {successMsg && (
        <MessageStrip design="Positive" hideCloseButton style={{ marginBottom: '1rem' }}>
          {successMsg}
        </MessageStrip>
      )}

      {/* Benutzerliste */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <CardHeader titleText={`Benutzer (${users.length})`} />
        <Table
          columns={
            <>
              <TableColumn><Text>Name</Text></TableColumn>
              <TableColumn><Text>E-Mail</Text></TableColumn>
              <TableColumn><Text>Rolle</Text></TableColumn>
              <TableColumn><Text>Erstellt am</Text></TableColumn>
              <TableColumn><Text>Aktionen</Text></TableColumn>
            </>
          }
        >
          {users.map(user => {
            const rc = roleConfig[user.organization_role] || roleConfig[OrganizationRole.MEMBER];
            return (
              <TableRow key={user.id}>
                <TableCell><Text>{user.name}</Text></TableCell>
                <TableCell><Text>{user.email}</Text></TableCell>
                <TableCell>
                  <ObjectStatus state={rc.state as any} showDefaultIcon>
                    {rc.label}
                  </ObjectStatus>
                </TableCell>
                <TableCell>
                  <Text>{new Date(user.created_at).toLocaleDateString('de-DE')}</Text>
                </TableCell>
                <TableCell>
                  <FlexBox>
                    <Button
                      design="Transparent"
                      icon="edit"
                      tooltip="Bearbeiten"
                      onClick={() => handleOpenEdit(user)}
                      style={{ marginRight: '0.25rem' }}
                    />
                    {user.organization_role !== OrganizationRole.OWNER && (
                      <Button
                        design="Transparent"
                        icon="delete"
                        tooltip="Entfernen"
                        onClick={() => handleDelete(user)}
                      />
                    )}
                  </FlexBox>
                </TableCell>
              </TableRow>
            );
          })}
        </Table>
      </Card>

      {/* Berechtigungstabelle */}
      <Card>
        <CardHeader titleText="Rollenberechtigungen" />
        <Table
          columns={
            <>
              <TableColumn><Text>Berechtigung</Text></TableColumn>
              <TableColumn><Text>Inhaber</Text></TableColumn>
              <TableColumn><Text>Administrator</Text></TableColumn>
              <TableColumn><Text>Mitglied</Text></TableColumn>
              <TableColumn><Text>Betrachter</Text></TableColumn>
            </>
          }
        >
          {permissionsTable.map(row => (
            <TableRow key={row.action}>
              <TableCell><Text>{row.action}</Text></TableCell>
              <TableCell>
                <Icon name={row.owner ? 'accept' : 'decline'} style={{ color: row.owner ? 'var(--sapPositiveColor)' : 'var(--sapNegativeColor)' }} />
              </TableCell>
              <TableCell>
                <Icon name={row.admin ? 'accept' : 'decline'} style={{ color: row.admin ? 'var(--sapPositiveColor)' : 'var(--sapNegativeColor)' }} />
              </TableCell>
              <TableCell>
                <Icon name={row.member ? 'accept' : 'decline'} style={{ color: row.member ? 'var(--sapPositiveColor)' : 'var(--sapNegativeColor)' }} />
              </TableCell>
              <TableCell>
                <Icon name={row.viewer ? 'accept' : 'decline'} style={{ color: row.viewer ? 'var(--sapPositiveColor)' : 'var(--sapNegativeColor)' }} />
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </Card>

      {/* Dialog zum Anlegen/Bearbeiten */}
      <Dialog
        open={dialogOpen}
        headerText={editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer anlegen'}
        onAfterClose={() => setDialogOpen(false)}
        footer={
          <Bar
            endContent={
              <FlexBox>
                <Button design="Transparent" onClick={() => setDialogOpen(false)} style={{ marginRight: '0.5rem' }}>
                  Abbrechen
                </Button>
                <Button design="Emphasized" onClick={handleSave}>
                  {editingUser ? 'Speichern' : 'Anlegen'}
                </Button>
              </FlexBox>
            }
          />
        }
      >
        <div style={{ padding: '1rem', minWidth: '400px' }}>
          {error && (
            <MessageStrip design="Negative" hideCloseButton style={{ marginBottom: '1rem' }}>
              {error}
            </MessageStrip>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Name</Label>
            <Input
              value={formData.name}
              onInput={(e: any) => setFormData({ ...formData, name: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>E-Mail</Label>
            <Input
              type="Email"
              value={formData.email}
              onInput={(e: any) => setFormData({ ...formData, email: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <Label required={!editingUser} style={{ display: 'block', marginBottom: '0.25rem' }}>
              {editingUser ? 'Neues Passwort (leer lassen um es nicht zu ändern)' : 'Passwort'}
            </Label>
            <Input
              type="Password"
              value={formData.password}
              onInput={(e: any) => setFormData({ ...formData, password: e.target.value })}
              placeholder={editingUser ? 'Leer lassen wenn unverändert' : 'Mind. 6 Zeichen'}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Rolle</Label>
            <Select
              onChange={(e: any) => setFormData({ ...formData, organization_role: e.detail.selectedOption.value as OrganizationRole })}
              style={{ width: '100%' }}
            >
              <Option value={OrganizationRole.VIEWER} selected={formData.organization_role === OrganizationRole.VIEWER}>Betrachter</Option>
              <Option value={OrganizationRole.MEMBER} selected={formData.organization_role === OrganizationRole.MEMBER}>Mitglied</Option>
              <Option value={OrganizationRole.ADMIN} selected={formData.organization_role === OrganizationRole.ADMIN}>Administrator</Option>
              <Option value={OrganizationRole.OWNER} selected={formData.organization_role === OrganizationRole.OWNER}>Inhaber</Option>
            </Select>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
